<template>
  <div class="quiz-layout">
    <h2 class="quiz-layout__title">{{ props.title }}</h2>
    <p class="quiz-layout__subtitle">
      {{ props.subtitle }}
    </p>

    <Loader v-if="loading" />
    <div class="items" v-if="!props.loading">
      <div
        v-for="item in props.data"
        :key="item.id"
        :class="{
          item: true,
          'item-selected': selected?.includes(item.id),
        }"
        @click="onClickItem(item)"
      >
        <div class="item__top-container">
          <div class="item__top">
            <div
              :class="{
                item__checkbox: true,
                'item__checkbox-selected': selected?.includes(item.id),
              }"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                class="icon-svg"
                width="12px"
                height="12px"
              >
                <path
                  stroke="#FFFFFF"
                  fill="#ffffff"
                  stroke-width="0"
                  stroke-alignment="inside"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class=""
                  d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
                ></path>
              </svg>
            </div>
            <span>{{ item.name }}</span>
          </div>
        </div>
        <div class="item__image-wrapper">
          <img :src="item.imagePath" :alt="item.name" class="item__image" />
        </div>
      </div>
    </div>
    <div
      :class="{
        'quiz-nav': true,
        'quiz-nav-menu-closed': menuStore.closeMenu,
        'quiz-nav-menu-opened': !menuStore.closeMenu,
      }"
    >
      <svg
        v-if="props.percent"
        class="quiz-nav-progress"
        style="width: 100%; height: 4px"
      >
        <rect :width="props.percent + '%'" height="4" fill="#AD6F69" />
      </svg>

      <button @click="emit('onBack')" class="quiz-button quiz-button-outline">
        {{ $t("project_details.style_quiz.back") }}
      </button>
      <button @click="onSubmit()" class="quiz-button">
        {{ props.nextButtonTitle ?? $t("project_details.style_quiz.next") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, inject } from "vue";
import Loader from "@/components/loader";
import { useMenuStore } from "@/stores/menu.store";
import AlertHelper from "@/helpsers/alert.helper";

const menuStore = useMenuStore();

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
  },
  // id, name, imagePath
  data: {
    type: Array,
  },
  multiple: {
    type: Boolean,
    required: true,
  },
  nextButtonTitle: {
    type: String,
  },
  percent: {
    type: Number,
  },
  initialSelected: {
    type: Array,
  },
});

const selected = ref(props.initialSelected ?? []);
const onClickItem = (item) => {
  if (!props.multiple) {
    selected.value = [item.id];
    return;
  }
  if (selected.value.includes(item.id)) {
    selected.value = selected.value.filter((s) => s !== item.id);
    return;
  }
  selected.value.push(item.id);
};

const emit = defineEmits(["onBack", "onNext"]);

const alert = inject("alert");

function onSubmit() {
  if (selected.value.length <= 0) {
    AlertHelper.show(
      alert,
      "Wymagany wybór",
      "Wybierz opcję z listy zanim przejdziesz dalej!",
      "error"
    );
    return;
  }

  /**
   * Result
   * id, imagePath, name, tags
   */
  const result = props.data.filter((item) => selected.value.includes(item.id));
  emit("onNext", result);
}
</script>

<style scoped lang="scss">
@use "@/css/quiz.scss";
.quiz-layout {
  height: 100%;
  padding-top: 10px;
  padding-bottom: 77px;
  &__title {
    font-weight: 600;
    font-size: 2.5rem;
    line-height: 5rem;
    text-align: center;
  }
  &__subtitle {
    font-weight: 400;
    font-size: 2rem;
    line-height: 2.5rem;
    text-align: center;
    color: #767a80;
  }
}

.items {
  margin: 28px 0;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-rows: 1fr;
  gap: 40px;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 1300px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
.item {
  background: #ffffff;
  border: 1px solid #cacaca;
  border-radius: 10px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  cursor: pointer;
  overflow: hidden;

  &-selected {
    border: 1px solid #ad6f69;
  }

  &__top-container {
    height: 100%;
  }

  &__top {
    display: flex;
    gap: 10px;
    padding: 20px;
    font-weight: 600;
    font-size: 2rem;
    line-height: 2.5rem;
  }
  &__checkbox {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #cacaca;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    &-selected {
      background-color: #ad6f69;
      border: 1px solid #ad6f69;
    }
  }
  &__image-wrapper {
    height: 220px;
    width: 100%;
  }
  &__image {
    object-fit: cover;
    width: 100%;
    height: 220px;
  }
}

.quiz-button {
  height: 45px;
  min-width: 120px;
  padding: 0 2rem;
}

.quiz-nav {
  position: fixed;
  bottom: 0;
  right: 0;
  height: 77px;
  &-menu-opened {
    width: calc(100% - 320px);
    @media screen and (max-width: 1199px) {
      width: calc(100% - 190px);
    }
  }
  &-menu-closed {
    width: calc(100% - 80px);
  }

  @media screen and (max-width: 767px) {
    width: calc(100%);
  }

  padding: 2rem 0;

  display: flex;
  background-color: white;
  align-items: center;
  justify-content: center;
  gap: 20px;
  &-progress {
    position: absolute;
    top: 0;
    left: 0;
    height: 4px;
    width: 100%;
    background-color: #e4e6e4;
  }
}
</style>
